import type { FeatureKey } from './state'
import type { Simulation } from './simulation'

/**
 * Die fünfzehn Modellstufen — als KATALOG und nicht als fünfzehn Stellen in der
 * Oberfläche.
 *
 * Jeder Eintrag sagt, was der Schalter tut, was sein Ausschalten bedeutet und —
 * das ist der ungewöhnliche Teil — in welcher Betriebsart er überhaupt wirkt.
 * Ohne diese letzte Angabe legt jemand im gerechneten Gang die Beckenkippung um,
 * sieht keine Zahl wandern und hält den Schalter für kaputt.
 *
 * Der Katalog steht im Kern und nicht in der Seite: die Prüfungen lesen ihn
 * mit, und ein Schalter, der hier fehlt, fällt dort auf.
 */

/** In welcher Betriebsart eine Stufe etwas wegzunehmen hat. */
export type FeatureScope = 'beide' | 'gerechnet' | 'aufgezeichnet'

export interface FeatureSpec {
  key: FeatureKey
  group: string
  label: string
  /** Was die Stufe tut. */
  note: string
  /** Was ihr Ausschalten bedeutet. */
  off: string
  scope: FeatureScope
}

export const FEATURE_GROUPS = ['Becken', 'Kinematik', 'Kinetik', 'Energie'] as const

export const FEATURES: readonly FeatureSpec[] = [
  {
    key: 'pelSag',
    group: 'Becken',
    label: 'Beckenkippung (vor/zurück)',
    note: 'Nickachse: das Becken kippt im Zyklus nach vorn und zurück. Die Amplitude hängt am Regler Beckenkippung.',
    off: 'fester Anteversionswinkel — das Becken steht.',
    scope: 'aufgezeichnet',
  },
  {
    key: 'pelFro',
    group: 'Becken',
    label: 'Beckenobliquität (seitlich)',
    note: 'Rollachse: die Beckenhälfte der Schwungseite senkt sich ab, das Becken verlagert sich zum Standbein.',
    off: 'das Becken bleibt waagerecht und steht seitlich still.',
    scope: 'beide',
  },
  {
    key: 'pelTra',
    group: 'Becken',
    label: 'Beckenrotation (horizontal)',
    note: 'Gierachse: das Becken dreht mit dem Schwungbein nach vorn, der Rumpf hält gegen.',
    off: 'keine Horizontaldrehung — der Schritt kommt allein aus dem Bein.',
    scope: 'beide',
  },
  {
    key: 'hipAbd',
    group: 'Kinematik',
    label: 'Abduktorenführung',
    note: 'Die Hüfte führt den Fuss in der Frontalebene auf seine Spur, während sich das Becken darüber verlagert.',
    off: 'das Bein hängt am Becken, und der Fuss wandert zur Mittellinie.',
    scope: 'gerechnet',
  },
  {
    key: 'frontal',
    group: 'Kinematik',
    label: 'Frontal & Transversal',
    note: 'Ab- und Anspreizung sowie Innen- und Aussenrotation in Hüfte, Knie und Sprunggelenk.',
    off: 'reines Sagittalmodell — die Maschine bewegt sich nur in einer Ebene.',
    scope: 'beide',
  },
  {
    key: 'mtp',
    group: 'Kinematik',
    label: 'Zehengrundgelenk',
    note: 'Dreiteiliger Fuss mit MTP-Achse — echte Fersenablösung und Vorfussrocker. Beim Gen5 standardmässig aus: Foot_V5 ist starr.',
    off: 'der Fuss ist ein starres Brett — der Stand des Gen5.',
    scope: 'aufgezeichnet',
  },
  {
    key: 'trunk',
    group: 'Kinematik',
    label: 'Rumpfkette',
    note: 'Lenden- und Brustwirbelsäule als eigene Gelenke mit Gegenrotation zum Becken.',
    off: 'starrer Oberkörper — er folgt dem Becken 1:1, und der Drall um die Hochachse wächst.',
    scope: 'beide',
  },
  {
    key: 'gaze',
    group: 'Kinematik',
    label: 'Kopfstabilisierung',
    note: 'Der Kopf folgt der Rumpfdrehung nur zu 35 % — sonst sähen Kamera und Lagesensor die volle Rumpfschwingung.',
    off: 'der Kopf sitzt starr am Brustkorb.',
    scope: 'beide',
  },
  {
    key: 'arms',
    group: 'Kinematik',
    label: 'Armschwung',
    note: 'Schulter und Ellbogen schwingen gegenläufig zu den Beinen und dämpfen den Drall.',
    off: 'die Arme stehen in Ruhelage.',
    scope: 'beide',
  },
  {
    key: 'zmp',
    group: 'Kinetik',
    label: 'Druckmittelpunkt aus dem Momentengleichgewicht',
    note: 'Der Angriffspunkt der Bodenreaktion folgt der Beschleunigung des Schwerpunkts.',
    off: 'eine fest vorgegebene Abrollkurve Ferse → Vorfuss, die von der Bewegung nichts weiss.',
    scope: 'beide',
  },
  {
    key: 'comply',
    group: 'Kinetik',
    label: 'Nachgiebiges Standbein',
    note: 'Fersenpolster, Sohle und Weichteile glätten die Beinlängen-Zwangsbedingung.',
    off: 'starres Bein — jede Sohlenkante schlägt voll auf die Rumpfbahn durch.',
    scope: 'beide',
  },
  {
    key: 'toeSpring',
    group: 'Kinetik',
    label: 'Zehenfeder statt Antrieb',
    note: 'Das Zehengrundgelenk nimmt über den Zyklus nur Energie auf — in jeder Gangart ist die Nettoarbeit negativ. Ein Motor wäre dort Masse an der schlechtesten Stelle des Beins: 0,8 kg am Vorfuss bringen bei einem Meter Abstand rund 0,8 kg·m² in die Hüftrechnung ein, mehr als der ganze Gewinn der proximalen Kniekonstruktion. Statt dessen eine progressive Feder mit Vorspannung und Anschlag: sie speichert, was der Boden hineindrückt, und gibt es beim Abheben zurück.',
    off: 'starre Zehe — die Energie verschwindet.',
    scope: 'beide',
  },
  {
    key: 'recSpring',
    group: 'Energie',
    label: 'Federspeicher je Gelenk',
    note: 'Eine Feder in Reihe zum Antrieb nimmt 55 % der Bremsleistung auf und gibt sie mit 80 % wieder ab — schnell, aber nur im eigenen Gelenk, begrenzt auf 60 J und mit einer Entladezeitkonstante von 0,5 s.',
    off: 'die Bremsleistung geht vollständig an den Zwischenkreis.',
    scope: 'beide',
  },
  {
    key: 'recBus',
    group: 'Energie',
    label: 'Rückspeisung in den Zwischenkreis',
    note: 'Was die Feder nicht nimmt, geht generatorisch über den Motor auf den gemeinsamen Kreis (85 %) und von dort zurück in mechanische Leistung (85 %). Dieser Speicher zerfällt nicht.',
    off: 'die Bremsenergie wird im Bremswiderstand verheizt.',
    scope: 'beide',
  },
  {
    key: 'hold',
    group: 'Energie',
    label: 'Halte- und Elektronikverluste',
    note: 'Ein Moment zu halten kostet Strom, auch ohne Bewegung: 0,0012 W je Nm² Kupferverlust, dazu 60 W Grundlast für Rechner, Regelung und Kühlung.',
    off: 'Halten ist umsonst — die untere Schranke, keine Auslegung.',
    scope: 'beide',
  },
]

export const featureSpec = (key: FeatureKey): FeatureSpec => FEATURES.find((f) => f.key === key)!

/**
 * Wirkt eine Stufe in dieser Betriebsart?
 *
 * Der gerechnete Gang hat keine Beckenkippung und kein Zehengrundgelenk — beide
 * Schalter haben dort nichts wegzunehmen. Der aufgezeichnete Gang setzt den Fuss
 * nicht, er liest Winkel ab; eine Abduktorenführung gibt es dort nicht. Das ist
 * kein Defekt, sondern der Unterschied zwischen den beiden Betriebsarten.
 */
export const featureApplies = (spec: FeatureSpec, generated: boolean): boolean =>
  spec.scope === 'beide' || (spec.scope === 'gerechnet') === generated

/* -------------------------------------------------------------------------
   Die Wirkung messen
   ------------------------------------------------------------------------- */

/**
 * Eine Kennzahl, an der man Modellstufen vergleicht.
 *
 * Als Katalog und nicht als feste Tabelle in der Seite: die Wirkungsübersicht
 * zeigt alle, und wer eine hinzufügt, fügt eine Zeile hinzu und keine Ansicht.
 */
export interface AblationMetric {
  key: string
  label: string
  unit: string
  digits: number
  read: (sim: Simulation) => number
  /**
   * Ob diese Kennzahl in dieser Betriebsart überhaupt GEMESSEN wird.
   *
   * Fehlt sie, ist sie immer messbar. Der Fall, für den es das Feld gibt: der
   * Abstand des Fusses zur Mittellinie folgt aus einer gesetzten Fussspur, und
   * die gibt es nur im gerechneten Gang. Im aufgezeichneten stünde dort eine
   * Null — und eine Null liest sich als „der Fuss steht auf der Mittellinie",
   * also als das Gegenteil dessen, was gemeint ist.
   */
  measured?: (sim: Simulation) => boolean
}

export const ABLATION_METRICS: readonly AblationMetric[] = [
  { key: 'speed', label: 'Geschwindigkeit', unit: 'm/s', digits: 2, read: (s) => s.metrics.speed },
  {
    key: 'grf',
    label: 'Grösste Bodenkraft',
    unit: '×KG',
    digits: 2,
    read: (s) => s.metrics.groundPeak,
  },
  {
    key: 'moment',
    label: 'Grösstes Gelenkmoment',
    unit: 'Nm',
    digits: 0,
    read: (s) => Math.max(...s.drives.map((d) => d.demand.moment.peak)),
  },
  {
    key: 'use',
    label: 'Ausnutzung der Momentgrenze',
    unit: '%',
    digits: 0,
    read: (s) => s.metrics.worstMomentUse * 100,
  },
  {
    key: 'drive',
    label: 'Antriebsarbeit je Zyklus',
    unit: 'J',
    digits: 0,
    read: (s) => s.power.workDriving,
  },
  { key: 'net', label: 'Nettoarbeit am Akku', unit: 'J', digits: 0, read: (s) => s.power.work },
  {
    key: 'recovered',
    label: 'Rückgewinnung',
    unit: '%',
    digits: 0,
    read: (s) => s.metrics.recovered * 100,
  },
  {
    key: 'power',
    label: 'Mittlere Leistung',
    unit: 'W',
    digits: 0,
    read: (s) => s.metrics.netPower,
  },
  {
    /*
      Die Zehenfeder wirkt auf KEINE der Zahlen darüber: sie speichert und gibt
      zurück, aber das Zehengrundgelenk hat keinen Antrieb und steht deshalb in
      keiner Leistungsbilanz. Ohne diese Spalte bewegte der Schalter nichts, und
      die Übersicht behauptete, er tue nichts.
    */
    key: 'toe',
    label: 'Zehenfeder zurück',
    unit: 'J',
    digits: 1,
    read: (s) => s.toe.returned,
  },
  {
    key: 'cot',
    label: 'Transportkosten',
    unit: '',
    digits: 3,
    read: (s) => s.metrics.transportCost,
  },
  {
    key: 'track',
    label: 'Fuss zur Mittellinie',
    unit: 'mm',
    digits: 0,
    read: (s) => s.gait.report.trackClearance * 1000,
    measured: (s) => s.state.generated,
  },
  {
    key: 'ground',
    label: 'Bodendurchdringung',
    unit: 'mm',
    digits: 1,
    read: (s) => s.metrics.groundError * 1000,
  },
]

export interface AblationRow {
  spec: FeatureSpec
  /** Ob die Stufe im Ausgangszustand an war. */
  was: boolean
  /** Ob sie in dieser Betriebsart überhaupt etwas bewirkt. */
  applies: boolean
  /** Je Kennzahl der Wert nach dem Umschalten. */
  values: Record<string, number>
  /** Je Kennzahl die Änderung gegenüber dem Ausgangszustand. */
  delta: Record<string, number>
  /**
   * Die grösste relative Änderung über alle Kennzahlen.
   *
   * Die eine Zahl, nach der sich sortieren lässt. Relativ und nicht absolut:
   * 40 Nm und 40 J sind sonst dasselbe Gewicht, und die Liste stünde nach
   * Einheiten geordnet statt nach Wirkung.
   */
  weight: number
}

export interface Ablation {
  base: Record<string, number>
  rows: AblationRow[]
  /** Die Kennzahlen, die in dieser Betriebsart gemessen werden. */
  metrics: AblationMetric[]
}

/**
 * Schaltet jede Stufe EINZELN um und misst, was sich ändert.
 *
 * Das ist die Antwort auf die Frage, die die fünfzehn Schalter aufwerfen und
 * einzeln nicht beantworten: was trägt ein Modellanteil zur Bilanz bei? Wer
 * sie von Hand durchprobiert, vergisst, welchen er schon hatte.
 *
 * Fünfzehn zusätzliche Durchläufe des Kerns. Das ist teuer genug, um es nicht
 * bei jedem Tastendruck zu tun, und billig genug, um es auf Knopfdruck zu tun —
 * deshalb steht es als Funktion hier und nicht in einem Effekt.
 */
export function runAblation(
  /** Rechnet den aktuellen Zustand mit UMGELEGTER Stufe durch. */
  toggle: (key: FeatureKey) => Simulation,
  current: Simulation,
): Ablation {
  /* Nur die Kennzahlen, die hier auch gemessen werden. Eine Spalte voller
     Nullen, weil die Grösse in dieser Betriebsart nicht entsteht, ist keine
     Auskunft — sie ist eine falsche. */
  const metrics = ABLATION_METRICS.filter((m) => m.measured?.(current) ?? true)

  const base: Record<string, number> = {}
  for (const metric of metrics) base[metric.key] = metric.read(current)

  const rows: AblationRow[] = FEATURES.map((spec) => {
    /* UMGELEGT, NICHT AUSGESCHALTET. Wer eine Stufe von Hand abgeschaltet hat,
       will wissen, was sie ihm BRINGT — nicht, was ihr Abschalten nochmals
       brächte. Die Tabelle zeigt deshalb immer den Weg zum anderen Zustand. */
    const sim = toggle(spec.key)
    const values: Record<string, number> = {}
    const delta: Record<string, number> = {}
    let weight = 0
    for (const metric of metrics) {
      const value = metric.read(sim)
      values[metric.key] = value
      delta[metric.key] = value - base[metric.key]
      const scale = Math.max(1e-9, Math.abs(base[metric.key]))
      weight = Math.max(weight, Math.abs(delta[metric.key]) / scale)
    }
    return {
      spec,
      was: current.state.features[spec.key],
      applies: featureApplies(spec, current.state.generated),
      values,
      delta,
      weight,
    }
  })

  return { base, rows, metrics }
}
